import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';

import { ReceiptService } from '../../../../services/accounting/receipt.service';
import { ContactSearchService } from '../../../../services/contacts/contact-search.service';
import { MatterSearchService } from '../../../../services/matters/matter-search.service';
import { NotyService } from '../../../../services/common/noty.service';
import { UserService } from '../../../../services/users/user.service';

interface Allocation {
  matterId: number;
  amount: number;
  applyTo: string;
  matterName: string;
  billId?: string;
}

interface MatterBalance {
  Charges: number;
  Payments: number;
  'Fee Credits': number;
  'Fee Total': number;
  Expenses: number;
  'Expense Payments': number;
  'Expense Credits': number;
  'Expense Total': number;
  Escrow: number;
  'Escrow Fee': number;
  'Escrow Costs': number;
  'Escrow Other': number;
}

interface WcStatus {
  Outstanding: number | string;
  'Paid Until': string;
  'Rate Type': string;
  'Weekly Rate': number | string;
}

@Component({
  selector: 'app-receipt-new',
  templateUrl: './receipt-new.component.html',
  styleUrls: ['./receipt-new.component.scss']
})
export class ReceiptNewComponent implements OnInit, OnDestroy {
  // URL parameters
  scanId: number | null = null;
  autoRedirectRuleId: number | null = null;
  documentId: number | null = null;
  initialMatterId: number | null = null;
  initialContactId: number | null = null;
  initialAmount: number = 0;
  lawpayPaymentId: number | null = null;

  // Form state
  payorId: number | null = null;
  payorName: string = '';
  paymentMethodId: number | null = null;
  amount: number = 0;
  consumed: number = 0;

  // Allocation state
  currentAllocation: {
    matterId: number | null;
    matterName: string;
    matterNumber: string;
    amount: number;
    applyTo: string;
    expense: string | null;
    billId: string | null;
  } = {
    matterId: null,
    matterName: '',
    matterNumber: '',
    amount: 0,
    applyTo: '',
    expense: null,
    billId: null
  };

  allocations: Array<[number, number, string, string, string | null]> = [];
  billIds: string[] = [];

  // Data
  paymentMethods: any[] = [];
  matters: any[] = [];
  balances: MatterBalance = {
    Charges: 0,
    Payments: 0,
    'Fee Credits': 0,
    'Fee Total': 0,
    Expenses: 0,
    'Expense Payments': 0,
    'Expense Credits': 0,
    'Expense Total': 0,
    Escrow: 0,
    'Escrow Fee': 0,
    'Escrow Costs': 0,
    'Escrow Other': 0
  };
  expenses: { [key: string]: { description: string; balance: number } } = {};
  wcStatus: WcStatus | null = null;
  due: number = 0;
  heading: string = '';
  referral: string | null = null;

  // Apply-to options
  applyTos: string[] = [];
  needEscrow: boolean = false;
  max: number = 1000000;

  // Scan preview
  scanPreviewUrl: SafeUrl | null = null;
  scanPreviewError: boolean = false;

  // Contact search
  contactSearchTerm: string = '';
  contactSearchResults: any[] = [];
  isSearchingContact: boolean = false;

  // Matter search
  matterSearchTerm: string = '';
  matterSearchResults: any[] = [];
  isSearchingMatter: boolean = false;

  // Loading states
  loading: boolean = false;
  saving: boolean = false;

  constructor(
    private receiptService: ReceiptService,
    private contactSearchService: ContactSearchService,
    private matterSearchService: MatterSearchService,
    private notyService: NotyService,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.userService.navbarComponent) {
      this.userService.navbarComponent.setAccountingMenu();
    }

    // Get URL parameters
    this.route.queryParams.subscribe(params => {
      this.scanId = params['scan_id'] ? +params['scan_id'] : null;
      this.autoRedirectRuleId = params['auto_redirect_rule_id'] ? +params['auto_redirect_rule_id'] : null;
      this.initialMatterId = params['matter_id'] ? +params['matter_id'] : null;
      this.initialContactId = params['contact_id'] ? +params['contact_id'] : null;
      this.initialAmount = params['full_amount'] ? +params['full_amount'] : 0;
      this.lawpayPaymentId = params['lawpay_payment_id'] ? +params['lawpay_payment_id'] : null;

      this.amount = this.initialAmount;
      this.initialize();
    });
  }

  ngOnDestroy(): void {}

  async initialize(): Promise<void> {
    this.loading = true;

    try {
      // Load payment methods
      await this.loadPaymentMethods();

      // If we have initial data from scan redirect, load it
      if (this.scanId) {
        const params: any = {
          scan_id: this.scanId,
          auto_redirect_rule_id: this.autoRedirectRuleId
        };
        if (this.initialContactId) params.contact_id = this.initialContactId;
        if (this.initialMatterId) params.matter_id = this.initialMatterId;
        if (this.initialAmount) params.full_amount = this.initialAmount;

        try {
          const data = await this.receiptService.getNewReceiptData(params);
          if (data) {
            if (data.author_id) {
              this.payorId = data.author_id;
              this.payorName = data.author_name || '';
              await this.loadMattersForPayor();
            }
            if (data.document_id) {
              this.documentId = data.document_id;
            }
            if (data.scan_preview_url) {
              this.scanPreviewUrl = this.sanitizer.bypassSecurityTrustUrl(data.scan_preview_url);
            }
          }
        } catch (error) {
          console.log('Could not load scan data:', error);
        }
      }

      // If we have an initial contact but no payor yet, set it
      if (this.initialContactId && !this.payorId) {
        this.payorId = this.initialContactId;
        await this.loadContactInfo(this.initialContactId);
        await this.loadMattersForPayor();
      }

      // If we have an initial matter, select it
      if (this.initialMatterId && this.payorId) {
        await this.selectMatterById(this.initialMatterId);
      }
    } catch (error) {
      console.error('Error initializing receipt:', error);
      this.notyService.notify('Error loading receipt data', 'error');
    } finally {
      this.loading = false;
    }
  }

  async loadPaymentMethods(): Promise<void> {
    try {
      this.paymentMethods = await this.receiptService.getPaymentMethods();
    } catch (error) {
      console.error('Error loading payment methods:', error);
      this.notyService.notify('Error loading payment methods', 'error');
    }
  }

  async loadContactInfo(contactId: number): Promise<void> {
    try {
      const info = await this.contactSearchService.getContactInfo(contactId);
      if (info && info.name) {
        this.payorName = info.name;
      }
    } catch (error) {
      console.error('Error loading contact info:', error);
    }
  }

  // Contact search
  searchContacts(term: string): void {
    if (!term || term.length < 2) {
      this.contactSearchResults = [];
      return;
    }

    this.isSearchingContact = true;
    this.contactSearchService.contactsFieldSearch(term).subscribe(
      results => {
        this.contactSearchResults = results;
        this.isSearchingContact = false;
      },
      error => {
        console.error('Error searching contacts:', error);
        this.isSearchingContact = false;
      }
    );
  }

  selectContact(contact: any): void {
    this.payorId = contact.id;
    this.payorName = contact.label || contact.name;
    this.contactSearchResults = [];
    this.contactSearchTerm = '';
    this.loadMattersForPayor();
  }

  clearPayor(): void {
    this.payorId = null;
    this.payorName = '';
    this.matters = [];
    this.clearAllocation();
  }

  async loadMattersForPayor(): Promise<void> {
    if (!this.payorId) return;

    try {
      const result = await this.receiptService.getMattersForContact(this.payorId, this.initialMatterId || undefined);
      this.matters = result || [];
    } catch (error) {
      console.error('Error loading matters:', error);
      this.matters = [];
    }
  }

  // Matter selection
  async selectMatter(matter: any[]): Promise<void> {
    // matter is [id, name, number, type, referral]
    const matterId = matter[0];
    const matterName = matter[1];
    const matterNumber = matter[2];
    const matterReferral = matter[4] || null;

    this.currentAllocation.matterId = matterId;
    this.currentAllocation.matterName = matterName;
    this.currentAllocation.matterNumber = matterNumber;
    this.referral = matterReferral;

    await this.loadMatterBalance(matterId, matterNumber);
  }

  async selectMatterById(matterId: number): Promise<void> {
    try {
      const result = await this.receiptService.getMatter(matterId);
      if (result && result.length > 0) {
        await this.selectMatter(result);
      }
    } catch (error) {
      console.error('Error selecting matter:', error);
    }
  }

  // Matter search for finder field
  searchMatters(term: string): void {
    if (!term || term.length < 2) {
      this.matterSearchResults = [];
      return;
    }

    this.isSearchingMatter = true;
    this.matterSearchService.mattersFieldSearch(term).subscribe(
      results => {
        this.matterSearchResults = results;
        this.isSearchingMatter = false;
      },
      error => {
        console.error('Error searching matters:', error);
        this.isSearchingMatter = false;
      }
    );
  }

  async selectMatterFromSearch(matter: any): Promise<void> {
    this.matterSearchResults = [];
    this.matterSearchTerm = '';
    await this.selectMatterById(matter.id);
  }

  async loadMatterBalance(matterId: number, matterNumber: string): Promise<void> {
    try {
      // Get payment method info if selected (for determining apply-to options)
      let pm: any = null;
      if (this.paymentMethodId) {
        const paymentMethod = await this.receiptService.getPaymentMethod(this.paymentMethodId);
        pm = paymentMethod && paymentMethod[0] ? paymentMethod[0] : null;
      }

      const result = await this.receiptService.getMatterBalance(matterId, matterNumber);
      if (result && result[0]) {
        const data = result[0];
        const rawSummary = data.summary || {};
        // Ensure all balance values are proper numbers (API may return strings)
        for (const key of Object.keys(this.balances)) {
          (this.balances as any)[key] = Number(rawSummary[key]) || 0;
        }
        this.wcStatus = data.wc || null;

        // Process expenses, excluding already allocated ones
        const rawExpenses = data.expenses && data.expenses[0] ? data.expenses[0] : {};
        this.expenses = {};
        for (const key of Object.keys(rawExpenses)) {
          if (!this.billIds.includes(key)) {
            this.expenses[key] = rawExpenses[key];
          }
        }

        // Determine apply-to options (only if payment method is selected)
        if (this.paymentMethodId) {
          this.applyTos = ['Escrow Fee', 'Escrow Costs', 'Escrow Other'];

          if (!pm?.is_escrow && !this.needEscrow) {
            // Add Fee option if there's a balance due and not WC
            if (this.balances.Payments + this.balances.Charges < 0 && !this.wcStatus) {
              this.applyTos.push('Fee');
            }
            // Add Expense option if there are expenses
            if (this.balances.Expenses + this.balances['Expense Payments'] !== 0 && Object.keys(this.expenses).length > 0) {
              this.applyTos.push('Expense');
            }
            // Add WC options if applicable
            if (this.wcStatus) {
              this.applyTos.push(this.wcStatus['Rate Type']);
            }
          }
        }

        // Get balance due
        const dueResult = await this.receiptService.getBalanceDue(matterNumber);
        if (dueResult && dueResult[0] !== undefined) {
          this.due = dueResult[0];
          this.heading = this.due < 0 ? 'Balance Due' : '';
        }
      }

      // Set default allocation amount
      this.currentAllocation.amount = this.getAvailable();
    } catch (error) {
      console.error('Error loading matter balance:', error);
    }
  }

  onPaymentMethodChange(): void {
    if (this.currentAllocation.matterId) {
      this.loadMatterBalance(this.currentAllocation.matterId, this.currentAllocation.matterNumber);
    }
  }

  onApplyToChange(): void {
    const applyTo = this.currentAllocation.applyTo;
    const available = this.getAvailable();
    let promptAmount = 0;

    if (applyTo === 'Contingent' || applyTo === 'Fee') {
      const feeDue = (this.balances.Charges + this.balances.Payments) * -1;
      promptAmount = Math.min(feeDue, available);
      this.max = feeDue;
    } else if (['Escrow Fee', 'Escrow Costs', 'Escrow Other'].includes(applyTo)) {
      promptAmount = available;
      this.max = available;
    } else if (applyTo === 'Expense') {
      const expenseBalances = Object.values(this.expenses).map(e => (e.balance || 0) * -1);
      const maxExpense = Math.max(...expenseBalances, 0);
      promptAmount = maxExpense;
      this.max = maxExpense;
    } else if (applyTo === 'Variable') {
      promptAmount = available;
      this.max = available;
    } else if (applyTo === 'Weekly' && this.wcStatus) {
      const weeklyRate = typeof this.wcStatus['Weekly Rate'] === 'number' ? this.wcStatus['Weekly Rate'] : 0;
      if (weeklyRate > 0) {
        promptAmount = Math.floor(available / weeklyRate) * weeklyRate;
        if (promptAmount === 0) promptAmount = available;
      } else {
        promptAmount = available;
      }
      this.max = available;
    }

    this.currentAllocation.amount = +promptAmount.toFixed(2);
  }

  onExpenseChange(): void {
    if (!this.currentAllocation.expense || !this.expenses[this.currentAllocation.expense]) return;

    const expense = this.expenses[this.currentAllocation.expense];
    const expenseAmount = (expense.balance || 0) * -1;
    const available = this.getAvailable();

    this.currentAllocation.amount = Math.min(expenseAmount, available);
    this.currentAllocation.billId = this.currentAllocation.expense;
  }

  getAvailable(): number {
    return +(this.amount - this.consumed).toFixed(2);
  }

  getBalance(): number {
    return this.amount - this.consumed;
  }

  isNumber(val: any): boolean {
    return typeof val === 'number' && !isNaN(val);
  }

  canAddAllocation(): boolean {
    // Check if we're ready to add more allocations (have remaining balance)
    // The template handles whether to show matter selection vs allocation form
    return !!(
      this.paymentMethodId &&
      this.amount > 0 &&
      this.payorId &&
      this.amount > this.consumed
    );
  }

  canAcceptAllocation(): boolean {
    return !!(
      this.currentAllocation.matterId &&
      this.currentAllocation.applyTo &&
      this.currentAllocation.amount > 0 &&
      this.currentAllocation.amount <= this.max
    );
  }

  addAllocation(): void {
    if (!this.canAcceptAllocation()) return;

    const allocation: [number, number, string, string, string | null] = [
      this.currentAllocation.matterId!,
      this.currentAllocation.amount,
      this.currentAllocation.applyTo,
      this.currentAllocation.matterName,
      this.currentAllocation.billId
    ];

    this.allocations.push(allocation);

    if (this.currentAllocation.billId) {
      this.billIds.push(this.currentAllocation.billId);
    }

    // Check if any allocation is escrow
    this.needEscrow = this.allocations.some(a => ['Escrow Fee', 'Escrow Costs', 'Escrow Other'].includes(a[2]));

    // If escrow is needed, convert non-escrow allocations
    if (this.needEscrow) {
      this.allocations.forEach(a => {
        if (a[2] === 'Expense') a[2] = 'Escrow Costs';
        if (a[2] === 'Fee') a[2] = 'Escrow Fee';
        if (a[2] === 'Weekly') a[2] = 'Escrow Fee';
        if (a[2] === 'Variable') a[2] = 'Escrow Fee';
      });
    }

    // Update consumed
    this.consumed = this.allocations.reduce((sum, a) => sum + a[1], 0);

    // Reset current allocation
    this.clearAllocation();
  }

  clearAllocation(): void {
    this.currentAllocation = {
      matterId: null,
      matterName: '',
      matterNumber: '',
      amount: this.getAvailable(),
      applyTo: '',
      expense: null,
      billId: null
    };
    this.balances = {
      Charges: 0,
      Payments: 0,
      'Fee Credits': 0,
      'Fee Total': 0,
      Expenses: 0,
      'Expense Payments': 0,
      'Expense Credits': 0,
      'Expense Total': 0,
      Escrow: 0,
      'Escrow Fee': 0,
      'Escrow Costs': 0,
      'Escrow Other': 0
    };
    this.expenses = {};
    this.wcStatus = null;
    this.due = 0;
    this.heading = '';
    this.applyTos = [];
  }

  deleteAllocation(index: number): void {
    const allocation = this.allocations[index];

    // Remove bill ID if present
    if (allocation[4] && typeof allocation[4] === 'string') {
      const billIdIndex = this.billIds.indexOf(allocation[4]);
      if (billIdIndex > -1) {
        this.billIds.splice(billIdIndex, 1);
      }
    }

    this.allocations.splice(index, 1);

    // Recalculate consumed
    this.consumed = this.allocations.reduce((sum, a) => sum + a[1], 0);

    // Recalculate needEscrow
    this.needEscrow = this.allocations.some(a => ['Escrow Fee', 'Escrow Costs', 'Escrow Other'].includes(a[2]));
  }

  canSave(): boolean {
    // Require full allocation: amount must equal consumed (within floating point tolerance)
    const fullyAllocated = Math.abs(this.amount - this.consumed) < 0.01;
    return (
      this.amount > 0 &&
      this.consumed > 0 &&
      fullyAllocated &&
      this.allocations.length > 0 &&
      !this.saving
    );
  }

  isFullyAllocated(): boolean {
    return Math.abs(this.amount - this.consumed) < 0.01;
  }

  async saveTransaction(): Promise<void> {
    if (!this.canSave()) return;

    this.saving = true;

    const data: any = {
      record_transaction: true,
      document_id: this.documentId,
      scanid: this.scanId,
      lawpay_payment_id: this.lawpayPaymentId,
      new_receipt: JSON.stringify({
        amount: this.amount,
        payment_method_id: this.paymentMethodId,
        payor_id: this.payorId,
        contact_id: this.payorId,
        consumed: this.consumed
      }),
      contact_id: this.payorId,
      allocations: JSON.stringify(this.allocations)
    };

    try {
      const result = await this.receiptService.create(data);
      if (result && result.success) {
        this.notyService.notify('Receipt saved successfully', 'success');
        const redirectUrl = result.redirect_url || '/receipts';
        this.router.navigateByUrl(redirectUrl);
      } else {
        this.notyService.notify('Error saving receipt', 'error');
      }
    } catch (error) {
      console.error('Error saving transaction:', error);
      this.notyService.notify('Error saving receipt', 'error');
    } finally {
      this.saving = false;
    }
  }

  cancelTransaction(): void {
    this.router.navigate(['/receipts']);
  }
}
